// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { keepPreviousData, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

// Models
import { type SiteID } from '../../sites/models/site'

// Services
import { Employee, EmployeeID } from '../../employees/models/employee'
import { EmployeeCreateForm, EmployeeAreaSyncForm, EmployeeBuildingSyncForm, create, syncAreas, syncBuildings, EmployeeSearchForm, fetchByShift, archive, restore, checkArchive, EmployeeUpdateForm, update, EmployeeAccountUpdateForm, updateAccount } from '../employeeService'
import { useEventStore } from '../../common/eventStore'
import { ShiftID } from '../../shifts/models/shift'
import { EmployeeFilters, employeeKeys } from './useEmployeeQuery'
import { Pagination } from '../../common/models/pagination'
import { EmployeeShiftOffer } from '../../employee_shift/models/employeeShiftOffer'


export function useEmployeeCreateMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const eventStore = useEventStore()

    return useMutation<{ employee: Employee }, Error, { form: EmployeeCreateForm }>({
        mutationFn: async (employeeCreate) => {
            return create(toValue(siteId), employeeCreate.form)
        },
        onSuccess: async () => {
            eventStore.addCreateToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useEmployeeUpdateMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const eventStore = useEventStore()

    return useMutation<{ employee: Employee }, Error, { employeeId: EmployeeID, form: EmployeeUpdateForm }>({
        mutationFn: async (data) => {
            return update(toValue(siteId), data.employeeId, data.form)
        },
        onSuccess: async () => {
            eventStore.addUpdateToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useAreaEmployeeSyncMutation(siteId: MaybeRefOrGetter<SiteID>, employeeId: MaybeRefOrGetter<EmployeeID>) {
    const eventStore = useEventStore()

    return useMutation<{ employee: Employee }, Error, { form: EmployeeAreaSyncForm }>({
        mutationFn: async (areaUpdate) => {
            return syncAreas(toValue(siteId), toValue(employeeId), areaUpdate.form)
        },
        onSuccess: async () => {
            eventStore.addSyncToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useBuildingEmployeeSyncMutation(siteId: MaybeRefOrGetter<SiteID>, employeeId: MaybeRefOrGetter<EmployeeID>) {
    const eventStore = useEventStore()

    return useMutation<{ employee: Employee }, Error, { form: EmployeeBuildingSyncForm }>({
        mutationFn: async (areaUpdate) => {
            return syncBuildings(toValue(siteId), toValue(employeeId), areaUpdate.form)
        },
        onSuccess: async () => {
            eventStore.addSyncToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useEmployeeByShiftQuery(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>, filters: EmployeeFilters = {}, enabled: MaybeRefOrGetter<boolean> = true) {
    return useQuery<{ offers: EmployeeShiftOffer[], pagination: Pagination }, Error>({
        queryKey: employeeKeys.listByShift(shiftId, filters),
        queryFn: async () => {
            const form: EmployeeSearchForm = {
                perPage: filters.perPage ? toValue(filters.perPage) : 15,
                page: filters.page ? toValue(filters.page) : 1,
            }

            const name = toValue(filters.name)
            if (name) {
                form.name = name
            }

            if (filters.status) {
                form.status = toValue(filters.status)
            }

            return fetchByShift(toValue(siteId), toValue(shiftId), form)
        },
        placeholderData: keepPreviousData,
        enabled: enabled,
    })
}

export function useEmployeeByShiftQueryInfinite(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>, filters: EmployeeFilters = {}, enabled: MaybeRefOrGetter<boolean> = true) {
    return useInfiniteQuery({
        queryKey: employeeKeys.listByShift(shiftId, filters),
        queryFn: async ({ pageParam }) => {
            const form: EmployeeSearchForm = {
                perPage: filters.perPage ? toValue(filters.perPage) : 15,
                page: pageParam,
            }

            const name = toValue(filters.name)
            if (name) {
                form.name = name
            }

            if (filters.status) {
                form.status = toValue(filters.status)
            }

            return fetchByShift(toValue(siteId), toValue(shiftId), form)
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pagination.currentPage + 1

            if (nextPage > lastPage.pagination.totalPages) {
                return undefined
            }

            return nextPage
        },
        getPreviousPageParam: (firstPage) => {
            const previousPage = firstPage.pagination.currentPage - 1

            if (previousPage < 1) {
                return undefined
            }

            return previousPage
        },
        enabled: enabled,
    })
}

export function employeeArchiveCheckOptions(siteId: MaybeRefOrGetter<SiteID>, employeeId: MaybeRefOrGetter<EmployeeID | null>) {
    return queryOptions({
        queryKey: employeeKeys.checkArchive(employeeId),
        queryFn: async () => {
            return checkArchive(toValue(siteId), toValue(employeeId))
        },
    })
}

export function useEmployeeCheckArchiveQuery(siteId: MaybeRefOrGetter<SiteID>, employeeId: MaybeRefOrGetter<EmployeeID>) {
    return useQuery(employeeArchiveCheckOptions(siteId, employeeId))
}

export function useEmployeeArchiveMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { employeeId: EmployeeID }>({
        mutationFn: async (data) => {
            return archive(toValue(siteId), data.employeeId)
        },
        onSettled: async (_data, _error, variables) => {
            return await queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.employeeId) })
        },
        onSuccess: async () => {
            eventStore.addArchiveToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useEmployeeRestoreMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { employeeId: EmployeeID }>({
        mutationFn: async (data) => {
            return restore(toValue(siteId), data.employeeId)
        },
        onSettled: async (_data, _error, variables) => {
            return await queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.employeeId) })
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })

            eventStore.addRestoreToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useEmployeeAccountUpdateMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const eventStore = useEventStore()

    return useMutation<{ employee: Employee }, Error, { employeeId: EmployeeID, form: EmployeeAccountUpdateForm }>({
        mutationFn: async (data) => {
            return updateAccount(toValue(siteId), data.employeeId, data.form)
        },
        onSuccess: async () => {
            eventStore.addUpdateToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}